import { useEffect, useState, type JSX } from 'react'
import { Link, useNavigate } from 'react-router'
import { AlertTriangle, ArrowRight } from 'lucide-react'
import { api } from '../../../../api/client.js'

export interface HeroBudgetCardProps {
  totalSpendUsd: number
  monthlySpendCapUsd: number
  percentUsed: number
  projectedSpendUsd: number
  state: 'safe' | 'will_exceed'
  willExceedDate: string | null
  dailyAverageUsd: number
  daysLeft: number
  softAlertThresholdPercent: number
  softAlertUsd: number

  scope: 'global' | 'agent'
  agentId?: string | null | undefined
  capExceededAction: 'fallback_to_free_models' | 'block_all_requests' | 'alert_only' | 'degrade_to_cheaper_model'
  resolvedFallbackTarget: { provider: string; model: string; isConfigured: boolean } | null
  fallbackWarning: string | null
  notifyChannels: string[]

  providerBanner?: {
    missingCount: number
    totalCount: number
    providerNames: string[]
    message: string
  } | null
  unresolvedIssuesCount?: number | undefined
  estimatedUsageCount?: number | undefined
  onOpenActivity?: () => void
}

interface LiveRateResponse {
  rpm: { current: number; limit: number; percent: number }
  tpm: { current: number; limit: number; percent: number }
  softAlertThresholdPercent: number
  tripsThisMonth: number
}

function formatTokens(n: number): string {
  if (n <= 0) return '0'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return n.toLocaleString()
}

export default function HeroBudgetCard({
  totalSpendUsd,
  monthlySpendCapUsd,
  percentUsed,
  projectedSpendUsd,
  state,
  willExceedDate,
  daysLeft,
  softAlertThresholdPercent,
  softAlertUsd,
  scope,
  agentId,
  capExceededAction,
  resolvedFallbackTarget,
  fallbackWarning,
  notifyChannels,
  providerBanner,
  unresolvedIssuesCount = 0,
  estimatedUsageCount = 0,
  onOpenActivity,
}: HeroBudgetCardProps): JSX.Element {
  const navigate = useNavigate()
  const [liveRate, setLiveRate] = useState<LiveRateResponse>({
    rpm: { current: 0, limit: 120, percent: 0 },
    tpm: { current: 0, limit: 60000, percent: 0 },
    softAlertThresholdPercent: softAlertThresholdPercent || 80,
    tripsThisMonth: 0,
  })

  // Poll live rate every 15s
  useEffect(() => {
    let mounted = true
    const params =
      scope === 'agent' && agentId
        ? `?scope=agent&agentId=${encodeURIComponent(agentId)}`
        : '?scope=global'

    const fetchRate = async () => {
      try {
        const res = await api.get<LiveRateResponse>(`/admin/ai/budget/live-rate${params}`)
        if (mounted) setLiveRate(res)
      } catch {
        // fail silently on background poll
      }
    }

    void fetchRate()
    const interval = setInterval(fetchRate, 15000)
    return () => {
      mounted = false
      clearInterval(interval)
    }
  }, [scope, agentId])

  const rpmOver = liveRate.rpm.percent >= 100
  const tpmOver = liveRate.tpm.percent >= 100
  const isLimited = rpmOver || tpmOver
  const isNearLimit =
    !isLimited &&
    (liveRate.rpm.percent >= liveRate.softAlertThresholdPercent ||
      liveRate.tpm.percent >= liveRate.softAlertThresholdPercent)

  const rateStatusText = isLimited ? 'Limited' : isNearLimit ? 'Near limit' : 'Normal'
  const rateDotColor = isLimited ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-emerald-500'

  const isSoftAlertCrossed = percentUsed >= softAlertThresholdPercent
  const isCapCrossed = percentUsed >= 100
  const progressBarColor = isCapCrossed
    ? 'bg-red-500'
    : isSoftAlertCrossed
      ? 'bg-amber-500'
      : 'bg-[#3454cf]'

  // Format Cap Exceeded Action label
  let actionLabel = 'Block all requests'
  if (capExceededAction === 'fallback_to_free_models') {
    actionLabel = resolvedFallbackTarget?.model
      ? `Fallback → ${resolvedFallbackTarget.provider} · ${resolvedFallbackTarget.model}`
      : 'Fallback to free models'
  } else if (capExceededAction === 'degrade_to_cheaper_model') {
    actionLabel = resolvedFallbackTarget?.model
      ? `Degrade → ${resolvedFallbackTarget.provider} · ${resolvedFallbackTarget.model}`
      : 'Degrade to cheaper model'
  } else if (capExceededAction === 'alert_only') {
    actionLabel = 'Alert only (do not block)'
  }

  const hasIssues = Boolean(providerBanner || fallbackWarning || unresolvedIssuesCount > 0)

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-2xs">
      <div className="grid grid-cols-1 gap-6 min-[900px]:grid-cols-3">
        {/* Left Section: Monthly Spend (approx 2/3) */}
        <div className="min-[900px]:col-span-2 flex flex-col justify-between">
          <div>
            <div className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">
              Monthly spend
            </div>

            <div className="mt-1 flex items-baseline justify-between">
              <div className="flex items-baseline gap-1.5">
                <span className="font-mono text-3xl font-bold tracking-tight text-gray-900">
                  ${totalSpendUsd.toFixed(2)}
                </span>
                <span className="font-mono text-sm font-medium text-gray-500">
                  of ${monthlySpendCapUsd.toFixed(2)}
                </span>
              </div>
              <span className="text-sm font-semibold text-gray-700">
                {percentUsed.toFixed(1)}% used
              </span>
            </div>

            {/* Single wide progress bar with soft alert tick */}
            <div className="relative mt-3 h-2.5 w-full rounded-full bg-gray-100 overflow-visible">
              <div
                className={`h-full rounded-full transition-all duration-300 ${progressBarColor}`}
                style={{ width: `${Math.min(percentUsed, 100)}%` }}
              />
              {/* Soft alert tick indicator */}
              {softAlertThresholdPercent > 0 && softAlertThresholdPercent < 100 && (
                <div
                  className="absolute top-[-2px] bottom-[-2px] w-[2px] bg-gray-400/80 z-10"
                  style={{ left: `${softAlertThresholdPercent}%` }}
                  title={`Soft alert threshold: ${softAlertThresholdPercent}% ($${softAlertUsd.toFixed(2)})`}
                />
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <span
                className={`h-2 w-2 rounded-full ${
                  state === 'safe' ? 'bg-emerald-500' : 'bg-red-500'
                }`}
              />
              <span>
                Projected month-end{' '}
                <strong className="font-semibold text-gray-700">
                  ${projectedSpendUsd.toFixed(2)}
                </strong>{' '}
                · {state === 'safe' ? 'Safe' : `Will exceed on ${willExceedDate ?? 'mid-month'}`}
              </span>
            </div>
            <div className="text-gray-500">
              {daysLeft} days left · Soft alert at ${softAlertUsd.toFixed(2)} ({softAlertThresholdPercent}%)
            </div>
          </div>

          {estimatedUsageCount > 0 && (
            <div className="mt-2 text-[11px] text-gray-400">
              * Includes {estimatedUsageCount} call{estimatedUsageCount > 1 ? 's' : ''} with estimated token usage (streaming disconnects or missing provider usage)
            </div>
          )}
        </div>

        {/* Right Section: Live rate, At the cap, Alerts (approx 1/3) */}
        <div className="border-t border-gray-100 pt-4 min-[900px]:border-t-0 min-[900px]:border-l min-[900px]:pl-6 min-[900px]:pt-0 flex flex-col justify-between space-y-3.5">
          {/* 1. Live rate */}
          <div>
            <div className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">
              Live rate
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs font-medium text-gray-800">
              <span className={`h-2 w-2 rounded-full ${rateDotColor} animate-pulse`} />
              <span>
                {liveRate.rpm.current} / {liveRate.rpm.limit} RPM · {formatTokens(liveRate.tpm.current)} / {formatTokens(liveRate.tpm.limit)} TPM
              </span>
              <span className="text-[11px] font-normal text-gray-500">
                {rateStatusText}
              </span>
            </div>
          </div>

          {/* 2. At the cap */}
          <div>
            <div className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">
              At the cap
            </div>
            <div className="mt-1 text-xs font-medium text-gray-800">
              {actionLabel}
            </div>
            {fallbackWarning && (
              <div className="mt-0.5 text-[11px] font-medium text-red-600">
                Fallback will block: no free/cheaper model configured
              </div>
            )}
          </div>

          {/* 3. Alerts */}
          <div>
            <div className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">
              Alerts
            </div>
            <div className="mt-1 text-xs font-medium text-gray-700">
              {notifyChannels.length > 0 ? notifyChannels.join(', ') : 'None configured'}
            </div>
          </div>
        </div>
      </div>

      {/* Slim Attention Strip / Footer Banner (only when needed) */}
      {hasIssues ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
            <span>
              {providerBanner ? (
                <>
                  <strong className="font-semibold">
                    {providerBanner.missingCount} of {providerBanner.totalCount} providers
                  </strong>{' '}
                  have no API key ({providerBanner.providerNames.join(', ')}).
                </>
              ) : fallbackWarning ? (
                <strong className="font-semibold">{fallbackWarning}</strong>
              ) : (
                <strong className="font-semibold">{unresolvedIssuesCount} unresolved items need attention.</strong>
              )}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {providerBanner && (
              <button
                type="button"
                onClick={() => navigate('/admin/ai-config')}
                className="font-semibold text-[#3454cf] hover:underline"
              >
                Configure providers →
              </button>
            )}
            {unresolvedIssuesCount > 1 && onOpenActivity && (
              <button
                type="button"
                onClick={onOpenActivity}
                className="font-semibold text-gray-700 hover:text-gray-900 hover:underline"
              >
                View all issues ({unresolvedIssuesCount}) →
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-4 flex items-center justify-end border-t border-gray-100 pt-2 text-xs">
          <Link
            to="/admin/ai-overview"
            className="inline-flex items-center gap-1 font-semibold text-[#3454cf] hover:underline"
          >
            Full trends in Dashboard <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}
    </div>
  )
}
